import { FC } from "react";

import {
  Alert,
  AlertDescription,
  Avatar,
  IconButton,
  Surface,
  Tag,
  Text,
  Tooltip,
  useCopyToClipboard,
} from "@zapier/design-system";

import { useAtomValue } from "jotai";
import {
  sessionAtom,
  accountAtom,
  currentAccountIdAtom,
} from "@zapier/canvas-state";
import styles from "./DevTools.module.css";

const toString = (value: any): string => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const ReadonlyControl: FC<{
  label: string;
  description: React.ReactNode;
  children: React.ReactNode;
  onCopy?: () => void;
  didCopy?: boolean;
}> = ({ label, description, children, onCopy, didCopy }) => {
  return (
    <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
      <div className={styles.devtoolsInner}>
        <div className={styles.flexRowSpaceBetween}>
          <Text type="Body3">{label}</Text>
          <div className={styles.flexRow}>
            <Text type="SmallPrint4">{children}</Text>
            {onCopy && (
              <div className={styles.copyButtonWrapper}>
                <Tooltip
                  content={didCopy ? "Copied to clipboard!" : `Copy ${label}`}
                  placement="left"
                >
                  <IconButton
                    aria-label={
                      didCopy ? "Copied to clipboard!" : `Copy ${label}`
                    }
                    name="actionCopy"
                    size="small"
                    variant="ghost"
                    onPress={onCopy}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
        <div className={styles.flexRowSpaceBetween}>
          <Text type="SmallPrint1" tag="div" className={styles.descriptionText}>
            {description}
          </Text>
        </div>
      </div>
    </Surface>
  );
};

export const AccountTabContent: FC = () => {
  const session = useAtomValue(sessionAtom);
  const accountId = useAtomValue(currentAccountIdAtom);
  const account = useAtomValue(accountAtom);

  const customuserId = toString(session?.is_logged_in && session.id);

  const accountIdAsString = toString(accountId);

  const currentAccountPlanType = account?.plan_info?.name || "";

  const [didCopyCustomuserId, copyCustomuserId] =
    useCopyToClipboard(customuserId);
  const [didCopyAccountId, copyAccountId] =
    useCopyToClipboard(accountIdAsString);

  // Get user name and avatar
  const firstName = session?.first_name || "";
  const lastName = session?.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim() || session?.email || "";
  const avatarUrl = session?.photo_url || undefined;

  // Get role for current account
  const currentAccountRole =
    session?.roles?.find((role) => role.account_id === accountId)?.role || null;
  const roleDisplayName =
    currentAccountRole === "super_admin"
      ? "Super Admin"
      : currentAccountRole === "owner"
        ? "Owner"
        : currentAccountRole === "admin"
          ? "Admin"
          : currentAccountRole === "member"
            ? "Member"
            : null;

  return (
    <div className={styles.devtoolsSectionWrapper}>
      <Alert title="" status="info" variant="compact">
        <AlertDescription>
          <Text type="MinimalPrint3">
            Details about your profile, account and current plan type.
          </Text>
        </AlertDescription>
      </Alert>
      {session?.is_logged_in && (
        <Surface backgroundColor="PrimeWhite" borderColor="GrayWarm5">
          <div className={styles.devtoolsInner}>
            <div className={styles.flexRowGap12}>
              <Avatar name={fullName} url={avatarUrl} size="medium" />
              <div className={styles.flexRowSpaceBetweenGap10}>
                <Text type="Body3">{fullName}</Text>
                {roleDisplayName && (
                  <Tag variant="BackgroundSelected" size="small">
                    {roleDisplayName}
                  </Tag>
                )}
              </div>
            </div>
          </div>
        </Surface>
      )}
      <ReadonlyControl
        label="Customuser ID"
        description="Your profile's customuserid."
        onCopy={copyCustomuserId}
        didCopy={didCopyCustomuserId}
      >
        {customuserId}
      </ReadonlyControl>
      <ReadonlyControl
        label="Current Account ID"
        description="Your current account."
        onCopy={copyAccountId}
        didCopy={didCopyAccountId}
      >
        {accountIdAsString}
      </ReadonlyControl>
      <ReadonlyControl
        label="Current Account Plan Type"
        description={<span>Your current account plan type.</span>}
      >
        {toString(currentAccountPlanType)}
      </ReadonlyControl>
    </div>
  );
};
